import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { referralService } from '../../services/referralService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ReportFilters from './components/ReportFilters';
import ReportStatistics from './components/ReportStatistics';
import ReferralDataTable from './components/ReferralDataTable';
import TrendsVisualization from './components/TrendsVisualization';
import ExportPanel from './components/ExportPanel';

const PractitionerReportsDashboard = () => {
  const navigate = useNavigate();
  const { userProfile, loading: authLoading, signOut } = useAuth();
  
  const [referrals, setReferrals] = useState([]);
  const [filteredReferrals, setFilteredReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showExportPanel, setShowExportPanel] = useState(false);

  const [filters, setFilters] = useState({
    dateRange: 'last30days',
    customDateFrom: '',
    customDateTo: '',
    referringDentist: 'all',
    toothNumber: '',
    referralReason: 'all',
    treatmentOutcome: 'all',
    status: 'all'
  });

  const [sortConfig, setSortConfig] = useState({
    field: 'submissionDate',
    direction: 'desc'
  });

  // Load practitioner's referrals
  useEffect(() => {
    if (!authLoading && userProfile) {
      if (!userProfile?.practitionerId) {
        setError('Aucun praticien associé à ce compte');
        setLoading(false);
        return;
      }
      loadReferrals();
    }
  }, [authLoading, userProfile]);

  // Apply filters when data or filters change
  useEffect(() => {
    applyFilters();
  }, [referrals, filters, sortConfig]);

  const loadReferrals = async () => {
    try {
      setLoading(true);
      setError('');

      // Load all referrals for this practitioner only
      const data = await referralService?.getAll(userProfile?.practitionerId);
      setReferrals(data || []);
    } catch (err) {
      console.error('Error loading referrals:', err);
      setError('Erreur lors du chargement des adressages');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...referrals];

    // Date range filter
    const now = new Date();
    let dateFrom = null;
    
    switch (filters?.dateRange) {
      case 'last30days':
        dateFrom = new Date(now?.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'last3months':
        dateFrom = new Date(now?.getFullYear(), now?.getMonth() - 3, now?.getDate());
        break;
      case 'last6months':
        dateFrom = new Date(now?.getFullYear(), now?.getMonth() - 6, now?.getDate());
        break;
      case 'lastyear':
        dateFrom = new Date(now?.getFullYear() - 1, now?.getMonth(), now?.getDate());
        break;
      case 'custom':
        if (filters?.customDateFrom) {
          dateFrom = new Date(filters?.customDateFrom);
        }
        break;
    }

    if (dateFrom) {
      filtered = filtered?.filter(ref => new Date(ref?.submissionDate) >= dateFrom);
    }

    if (filters?.dateRange === 'custom' && filters?.customDateTo) {
      const dateTo = new Date(filters?.customDateTo);
      filtered = filtered?.filter(ref => new Date(ref?.submissionDate) <= dateTo);
    }

    // Referring dentist filter
    if (filters?.referringDentist && filters?.referringDentist !== 'all') {
      filtered = filtered?.filter(ref => 
        ref?.referringDentistName?.toLowerCase()?.includes(filters?.referringDentist?.toLowerCase())
      );
    }

    // Tooth number filter
    if (filters?.toothNumber) {
      filtered = filtered?.filter(ref => 
        ref?.toothNumbers?.some(tooth => tooth?.toString()?.includes(filters?.toothNumber))
      );
    }

    // Referral reason filter
    if (filters?.referralReason && filters?.referralReason !== 'all') {
      filtered = filtered?.filter(ref => 
        ref?.referralReasons?.includes(filters?.referralReason)
      );
    }

    // Status filter (treatment outcome)
    if (filters?.treatmentOutcome && filters?.treatmentOutcome !== 'all') {
      filtered = filtered?.filter(ref => ref?.status === filters?.treatmentOutcome);
    }

    // Status filter
    if (filters?.status && filters?.status !== 'all') {
      filtered = filtered?.filter(ref => ref?.status === filters?.status);
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      const aValue = a?.[sortConfig?.field];
      const bValue = b?.[sortConfig?.field];
      
      if (aValue === bValue) return 0;
      
      const comparison = aValue > bValue ? 1 : -1;
      return sortConfig?.direction === 'asc' ? comparison : -comparison;
    });

    setFilteredReferrals(filtered);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleClearFilters = () => {
    setFilters({
      dateRange: 'last30days',
      customDateFrom: '',
      customDateTo: '',
      referringDentist: 'all',
      toothNumber: '',
      referralReason: 'all',
      treatmentOutcome: 'all',
      status: 'all'
    });
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev?.field === field && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const handleBackToDashboard = () => {
    navigate('/practitioner-dashboard');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A0A0F] via-[#1A1A1E] to-[#0A0A0F] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-300">Chargement des rapports...</p>
        </div>
      </div>
    );
  }

  if (error && referrals?.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A0A0F] via-[#1A1A1E] to-[#0A0A0F] flex items-center justify-center p-4">
        <div className="bg-[rgba(26,26,30,0.95)] backdrop-blur-2xl border border-white/10 rounded-2xl p-8 max-w-md w-full text-center shadow-2xl">
          <Icon name="AlertCircle" size={48} className="text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Erreur</h2>
          <p className="text-gray-300 mb-6">{error}</p>
          <Button onClick={handleBackToDashboard} variant="outline">
            Retour au tableau de bord
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Rapports d'Adressage - EndoReferral</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#0A0A0F] via-[#1A1A1E] to-[#0A0A0F]">
        {/* Header */}
        <div className="bg-[rgba(26,26,30,0.95)] backdrop-blur-2xl border-b border-white/10 sticky top-0 z-40 shadow-2xl">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleBackToDashboard}
                  className="text-gray-300 hover:text-white"
                >
                  <Icon name="ArrowLeft" size={20} />
                </Button>
                <div>
                  <h1 className="text-2xl font-bold text-white">Rapports d'Adressage</h1>
                  <p className="text-sm text-gray-400">Analyse et export de vos données</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowExportPanel(!showExportPanel)}
                  iconName="Download"
                >
                  Exporter
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  iconName="LogOut"
                >
                  Déconnexion
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
          {/* Statistics Cards */}
          <ReportStatistics referrals={filteredReferrals} allReferrals={referrals} />

          {/* Filters */}
          <ReportFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            referrals={referrals}
          />

          {/* Trends Visualization */}
          <TrendsVisualization referrals={filteredReferrals} />

          {/* Data Table */}
          <ReferralDataTable
            referrals={filteredReferrals}
            sortConfig={sortConfig}
            onSort={handleSort}
          />
        </div>

        {/* Export Panel Modal */}
        {showExportPanel && (
          <ExportPanel
            referrals={filteredReferrals}
            filters={filters}
            onClose={() => setShowExportPanel(false)}
          />
        )}
      </div>
    </>
  );
};

export default PractitionerReportsDashboard;